/* ─────────────────────────────────────────────────────────────
   Color math for the STUI color picker.

   Conversion and contrast are pure functions — no DOM, no React —
   so they can be reasoned about and unit-tested in isolation.
   parseColor handles hex / rgb() / hsl() itself and only falls back
   to the browser's own parser for the syntaxes a regex has no
   business trying to cover (named colors, hwb(), lab(), ...).
─────────────────────────────────────────────────────────────── */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const clamp01 = n => clamp(n, 0, 1);
const clamp255 = n => clamp(n, 0, 255);

/* ── Parsing ───────────────────────────────────────────────── */

const HEX_RE = /^#?([0-9a-f]{3,8})$/i;
const RGB_RE = /^rgba?\(\s*([-\d.]+%?)\s*[,\s]\s*([-\d.]+%?)\s*[,\s]\s*([-\d.]+%?)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i;
const HSL_RE = /^hsla?\(\s*([-\d.]+)(?:deg)?\s*[,\s]\s*([-\d.]+)%?\s*[,\s]\s*([-\d.]+)%?\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i;

/* A channel written either 0-255 or as a percentage. */
const channel = raw => (raw.endsWith('%')
  ? clamp255((parseFloat(raw) / 100) * 255)
  : clamp255(parseFloat(raw)));

/* Alpha is 0-1, or a percentage. Absent means opaque. */
const alphaOf = raw => (raw === undefined
  ? 1
  : clamp01(raw.endsWith('%') ? parseFloat(raw) / 100 : parseFloat(raw)));

const parseHex = body => {
  const h = body.toLowerCase();
  const expand = s => s.split('').map(c => c + c).join('');
  let hex;
  if (h.length === 3 || h.length === 4) hex = expand(h);
  else if (h.length === 6 || h.length === 8) hex = h;
  else return null;
  const n = p => parseInt(hex.slice(p, p + 2), 16);
  return {
    r: n(0), g: n(2), b: n(4),
    a: hex.length === 8 ? Math.round((n(6) / 255) * 1000) / 1000 : 1,
  };
};

/* Last resort: hand the string to the browser and read back what it
   made of it. Two different sentinels, because an unparseable value
   leaves fillStyle untouched rather than throwing. */
const parseViaBrowser = input => {
  if (typeof document === 'undefined') return null;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;
  const read = sentinel => {
    ctx.fillStyle = sentinel;
    ctx.fillStyle = input;
    return ctx.fillStyle;
  };
  const a = read('#000000');
  const b = read('#ffffff');
  if (a !== b) return null;            // rejected — fillStyle kept both sentinels
  return parseColor(a, true);
};

/**
 * Parse any reasonable CSS color into { r, g, b, a }.
 * Returns null when the string isn't a color.
 */
export function parseColor(input, skipBrowserFallback = false) {
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (!s) return null;

  const hex = s.match(HEX_RE);
  if (hex) return parseHex(hex[1]);

  const rgb = s.match(RGB_RE);
  if (rgb) {
    return {
      r: Math.round(channel(rgb[1])),
      g: Math.round(channel(rgb[2])),
      b: Math.round(channel(rgb[3])),
      a: alphaOf(rgb[4]),
    };
  }

  const hsl = s.match(HSL_RE);
  if (hsl) {
    const { r, g, b } = hslToRgb({
      h: parseFloat(hsl[1]),
      s: clamp(parseFloat(hsl[2]), 0, 100) / 100,
      l: clamp(parseFloat(hsl[3]), 0, 100) / 100,
    });
    return { r, g, b, a: alphaOf(hsl[4]) };
  }

  return skipBrowserFallback ? null : parseViaBrowser(s);
}

/* ── Conversion ────────────────────────────────────────────── */

/** { r, g, b } (0-255) → { h (0-360), s (0-1), v (0-1) } */
export function rgbToHsv({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn)      h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else                 h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

/** { h, s, v } → { r, g, b } (0-255) */
export function hsvToRgb({ h, s, v }) {
  const hh = ((h % 360) + 360) % 360 / 60;
  const c = clamp01(v) * clamp01(s);
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = clamp01(v) - c;
  const [r, g, b] = hh < 1 ? [c, x, 0]
    : hh < 2 ? [x, c, 0]
    : hh < 3 ? [0, c, x]
    : hh < 4 ? [0, x, c]
    : hh < 5 ? [x, 0, c]
    : [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

/** { r, g, b } (0-255) → { h (0-360), s (0-1), l (0-1) } */
export function rgbToHsl({ r, g, b }) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === rn)      h = ((gn - bn) / d) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else                 h = (rn - gn) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s, l };
}

/** { h, s, l } → { r, g, b } (0-255) */
export function hslToRgb({ h, s, l }) {
  const hh = ((h % 360) + 360) % 360 / 60;
  const c = (1 - Math.abs(2 * clamp01(l) - 1)) * clamp01(s);
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = clamp01(l) - c / 2;
  const [r, g, b] = hh < 1 ? [c, x, 0]
    : hh < 2 ? [x, c, 0]
    : hh < 3 ? [0, c, x]
    : hh < 4 ? [0, x, c]
    : hh < 5 ? [x, 0, c]
    : [c, 0, x];
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
}

/* ── Formatting ────────────────────────────────────────────── */

const hex2 = n => Math.round(clamp255(n)).toString(16).padStart(2, '0');

/** #rrggbb — alpha is dropped. */
export const toHex = ({ r, g, b }) => `#${hex2(r)}${hex2(g)}${hex2(b)}`;

/** #rrggbb, or #rrggbbaa when not fully opaque. */
export const toHex8 = ({ r, g, b, a = 1 }) =>
  (a >= 1 ? toHex({ r, g, b }) : `#${hex2(r)}${hex2(g)}${hex2(b)}${hex2(a * 255)}`);

/** rgb(...) when opaque, rgba(...) otherwise. */
export const toRgbString = ({ r, g, b, a = 1 }) => (a >= 1
  ? `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`
  : `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Math.round(a * 100) / 100})`);

/** hsl(...) / hsla(...), rounded to whole degrees and percents. */
export const toHslString = ({ r, g, b, a = 1 }) => {
  const { h, s, l } = rgbToHsl({ r, g, b });
  const parts = `${Math.round(h)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%`;
  return a >= 1 ? `hsl(${parts})` : `hsla(${parts}, ${Math.round(a * 100) / 100})`;
};

/* ── Contrast (WCAG 2.1) ───────────────────────────────────── */

const toLinear = c => {
  const n = c / 255;
  return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
};

/** Relative luminance, 0 (black) to 1 (white). Ignores alpha. */
export const luminance = ({ r, g, b }) =>
  0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

/** Flatten a translucent color onto an opaque background. */
export const compositeOver = (fg, bg) => {
  const a = fg.a === undefined ? 1 : clamp01(fg.a);
  return {
    r: fg.r * a + bg.r * (1 - a),
    g: fg.g * a + bg.g * (1 - a),
    b: fg.b * a + bg.b * (1 - a),
    a: 1,
  };
};

/** Contrast ratio between two colors, 1 to 21. Alpha is composited onto `bg`. */
export const contrastRatio = (fg, bg) => {
  const l1 = luminance(compositeOver(fg, bg));
  const l2 = luminance(bg);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
};

/** 'AAA' | 'AA' | 'Fail' for body text (or large text at 3:1 / 4.5:1). */
export const wcagLevel = (ratio, large = false) => {
  if (ratio >= (large ? 4.5 : 7)) return 'AAA';
  if (ratio >= (large ? 3 : 4.5)) return 'AA';
  return 'Fail';
};

/**
 * What each WCAG 2.1 category demands. `small` is body text; `large` is
 * 18pt and up, or 14pt bold and up; `nontext` covers UI component
 * boundaries and meaningful graphics, which have an AA bar and no AAA one.
 */
export const WCAG_THRESHOLDS = {
  small:   { label: 'Small text',       aa: 4.5, aaa: 7 },
  large:   { label: 'Large text',       aa: 3,   aaa: 4.5 },
  nontext: { label: 'UI and graphics',  aa: 3,   aaa: null },
};

/** 'AAA' | 'AA' | 'Fail' for one category — see WCAG_THRESHOLDS. */
export const wcagVerdict = (ratio, category = 'small') => {
  const bar = WCAG_THRESHOLDS[category] || WCAG_THRESHOLDS.small;
  if (bar.aaa !== null && ratio >= bar.aaa) return 'AAA';
  if (ratio >= bar.aa) return 'AA';
  return 'Fail';
};
